import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { OcTenant, OcPermissionKey } from "@oc/types";

interface OcSessionState {
  tenant: OcTenant | null;
  userName: string | null;
  userEmail: string | null;
  permissions: OcPermissionKey[];
  isTrial: boolean;
  trialDaysRemaining: number | null;
  onboardingComplete: boolean;
  sidebarCollapsed: boolean;
  isImpersonating: boolean;
  impersonatedBy: string | null;
  setTenant: (tenant: OcTenant) => void;
  setUser: (name: string, email: string) => void;
  setPermissions: (permissions: OcPermissionKey[]) => void;
  setOnboardingComplete: (complete: boolean) => void;
  setImpersonation: (impersonatedBy: string | null) => void;
  toggleSidebar: () => void;
  clearSession: () => void;
}

export const useOcSessionStore = create<OcSessionState>()(
  persist(
    (set) => ({
      tenant: null,
      userName: null,
      userEmail: null,
      permissions: [],
      isTrial: false,
      trialDaysRemaining: null,
      onboardingComplete: true,
      sidebarCollapsed: false,
      isImpersonating: false,
      impersonatedBy: null,

      setUser: (name, email) => set({ userName: name, userEmail: email }),

      setTenant: (tenant) => {
        const now = Date.now();
        const trialDaysRemaining =
          tenant.endsAt
            ? Math.max(
                0,
                Math.ceil((new Date(tenant.endsAt).getTime() - now) / 86_400_000)
              )
            : null;
        set({
          tenant,
          isTrial: tenant.status === "trial",
          trialDaysRemaining,
        });
      },

      setPermissions: (permissions) => set({ permissions }),

      setOnboardingComplete: (complete) => set({ onboardingComplete: complete }),

      setImpersonation: (impersonatedBy) =>
        set({
          isImpersonating: impersonatedBy !== null,
          impersonatedBy,
        }),

      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),

      clearSession: () =>
        set({
          tenant: null,
          userName: null,
          userEmail: null,
          permissions: [],
          isTrial: false,
          trialDaysRemaining: null,
          onboardingComplete: true,
          isImpersonating: false,
          impersonatedBy: null,
        }),
    }),
    { name: "oc-session" }
  )
);
